import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  makeStyles,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  InputAdornment,
  Chip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import axios from "axios";
import moment from "moment-timezone";
import { URL as CLIENT_URL } from "../../config/config";

const PAGE_SIZE = 25;
const INDIGO = "#4f46e5";
const INDIGO_SOFT = "#eef0fd";
const RED = "#c0392b";
const AMBER = "#b26a00";

const COLUMNS = [
  { label: "Project" },
  { label: "Customer ID" },
  { label: "Billing" },
  { label: "Campaigns (enabled / paused / auto-paused)" },
  { label: "Disapproved", align: "right" },
  { label: "Spend 7d", align: "right" },
  { label: "Conversations 7d", align: "right" },
  { label: "Qualified uploads 7d", align: "right" },
  { label: "Failed uploads 7d", align: "right" },
  { label: "Qualified within 24h", align: "right" },
  { label: "Button" },
  { label: "WhatsApp" },
  { label: "Last billing sync" },
];

const fmtDate = (v) => (v ? moment(v).format("DD MMM YY, HH:mm") : "—");
const fmtNum = (v) => (v || 0).toLocaleString("en-IN");
const fmtSpend = (v) =>
  (v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f6f7fb",
    // ProjectsLayout's route container is height:100vh + overflow:hidden —
    // pages must own their scrolling or content below the fold is clipped.
    height: "100vh",
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
  },
  content: { maxWidth: 1720, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  title: { fontWeight: 700, color: "#111827", letterSpacing: -0.3 },
  subtitle: { color: "#6b7280", marginTop: 2 },
  headerBtn: {
    color: INDIGO,
    borderColor: "#e0e3ef",
    backgroundColor: "#fff",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { borderColor: INDIGO, backgroundColor: "#fbfbff" },
  },
  card: {
    borderRadius: 14,
    border: "1px solid #ebedf3",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    backgroundColor: "#fff",
  },
  searchCard: { padding: theme.spacing(2, 3), marginBottom: theme.spacing(2.5) },
  searchRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  // white-input override (theme forces grey pills globally)
  field: {
    minWidth: 320,
    "&.MuiTextField-root": {
      backgroundColor: "#fff",
      border: "1px solid #e3e7ef",
      borderRadius: 9,
      transition: "border-color .15s, box-shadow .15s",
    },
    "&.MuiTextField-root:hover": { borderColor: "#c7cede" },
    "&.MuiTextField-root:focus-within": {
      borderColor: INDIGO,
      boxShadow: `0 0 0 3px ${INDIGO}1f`,
    },
  },
  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 22px",
  },
  resultsTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 17,
    fontWeight: 700,
    color: "#1f2937",
  },
  countBadge: {
    backgroundColor: INDIGO_SOFT,
    color: INDIGO,
    borderRadius: 20,
    padding: "1px 11px",
    fontSize: 13,
    fontWeight: 700,
    marginLeft: 4,
  },
  pager: { display: "flex", alignItems: "center", gap: 10 },
  pagerBtn: {
    border: "1px solid #e3e7ef",
    borderRadius: 8,
    padding: 5,
    color: "#4b5563",
    "&:hover": { backgroundColor: "#f5f6fa" },
  },
  pagePill: {
    minWidth: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${INDIGO}`,
    color: INDIGO,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  tableWrap: { overflowX: "auto" },
  headCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    backgroundColor: "#fafbfc",
    color: "#6b7280",
    fontSize: 12.5,
    borderBottom: "1px solid #edeff3",
  },
  cell: { whiteSpace: "nowrap", fontSize: 13, color: "#374151" },
  num: { whiteSpace: "nowrap", fontSize: 13, color: "#374151", fontVariantNumeric: "tabular-nums" },
  mono: { fontFamily: "monospace", fontSize: 12.5, color: "#37474f" },
  link: { color: INDIGO, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
  row: { "&:hover": { backgroundColor: "#fafbff" } },
  empty: { padding: theme.spacing(9), textAlign: "center", color: "#9aa3b2" },
  error: { color: RED, padding: "0 22px 12px" },
}));

function LiveChip({ live }) {
  return live ? (
    <Chip size="small" label="Live" style={{ backgroundColor: "#e7f6ee", color: "#1e874b" }} />
  ) : (
    <Chip size="small" label="Not live" style={{ backgroundColor: "#fdecea", color: RED }} />
  );
}

function ButtonCell({ widget, monoClass }) {
  if (!widget?.status && !widget?.widgetId) return "—";
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {widget.status ? <Chip size="small" variant="outlined" label={widget.status} /> : null}
      {widget.widgetId ? <span className={monoClass}>{widget.widgetId}</span> : null}
    </Box>
  );
}

function GoogleAdsPilotConsolePage() {
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Only the latest request may write state, so quick paging can't show stale rows.
  const latestRequest = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: page + 1, limit: PAGE_SIZE });
      if (search) params.set("search", search);
      const res = await axios.get(
        `${CLIENT_URL}/superadmin/google-ads-pilot-console?${params.toString()}`
      );
      if (requestId !== latestRequest.current) return;
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      if (requestId !== latestRequest.current) return;
      setError(
        e?.response?.data?.message || e?.message || "Failed to load Google Ads pilot console data"
      );
      setItems([]);
      setTotal(0);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applySearch = (value) => {
    setSearch(value.trim());
    setPage(0);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    applySearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput("");
    applySearch("");
  };

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.header}>
          <Box>
            <Typography variant="h5" className={classes.title}>
              Google Ads Pilot Console
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              One row per project with a Google Ads account — billing, campaigns, conversion
              uploads and button/WhatsApp readiness.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            className={classes.headerBtn}
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Paper className={`${classes.card} ${classes.searchCard}`} elevation={0}>
          <form className={classes.searchRow} onSubmit={onSubmit}>
            <TextField
              size="small"
              variant="outlined"
              className={classes.field}
              placeholder="Project ID or customer ID"
              inputProps={{ "aria-label": "Search by project ID or customer ID" }}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" style={{ color: "#98a2b3" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button type="submit" variant="outlined" className={classes.headerBtn}>
              Search
            </Button>
          </form>
        </Paper>

        <Paper className={classes.card} elevation={0}>
          <Box className={classes.resultsHeader}>
            <Typography component="div" className={classes.resultsTitle}>
              Results
              <span className={classes.countBadge}>{fmtNum(total)}</span>
            </Typography>
            <Box className={classes.pager}>
              <Typography variant="body2" style={{ color: "#6b7280" }}>
                Showing {from}-{to} of {fmtNum(total)}
              </Typography>
              <IconButton
                className={classes.pagerBtn}
                size="small"
                aria-label="Previous page"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <span className={classes.pagePill}>{page + 1}</span>
              <IconButton
                className={classes.pagerBtn}
                size="small"
                aria-label="Next page"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {error ? (
            <Typography role="alert" className={classes.error}>
              {error}
            </Typography>
          ) : null}

          <TableContainer className={classes.tableWrap}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.label} className={classes.headCell} align={c.align || "left"}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={classes.empty}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={classes.empty}>
                      <MoveToInboxIcon style={{ fontSize: 52, color: "#cdd3de" }} />
                      <Typography style={{ marginTop: 8, fontWeight: 600, color: "#6b7280" }}>
                        {error ? "Could not load pilot accounts" : "No pilot accounts found"}
                      </Typography>
                      {search && !error ? (
                        <Typography variant="caption">Try a different project or customer ID.</Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((r) => (
                    <TableRow key={`${r.assistantId}-${r.customerId}`} hover className={classes.row}>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>
                        <Link
                          className={classes.link}
                          to={`/assistants?search=${encodeURIComponent(r.assistantId)}`}
                        >
                          {r.assistantId}
                        </Link>
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>
                        {r.customerId || "—"}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {r.billingStatus ? (
                          <Chip size="small" variant="outlined" label={r.billingStatus} />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={classes.num}>
                        {fmtNum(r.campaigns?.enabled)} / {fmtNum(r.campaigns?.paused)} /{" "}
                        {fmtNum(r.campaigns?.autoPaused)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.num}
                        style={r.campaigns?.disapproved > 0 ? { color: RED, fontWeight: 700 } : undefined}
                      >
                        {fmtNum(r.campaigns?.disapproved)}
                      </TableCell>
                      <TableCell align="right" className={classes.num}>
                        {fmtSpend(r.spend7d)}
                      </TableCell>
                      <TableCell align="right" className={classes.num}>
                        {fmtNum(r.conversations7d)}
                      </TableCell>
                      <TableCell align="right" className={classes.num}>
                        {fmtNum(r.qualifiedUploads7d)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.num}
                        style={r.failedUploads7d > 0 ? { color: AMBER, fontWeight: 700 } : undefined}
                      >
                        {fmtNum(r.failedUploads7d)}
                      </TableCell>
                      <TableCell align="right" className={classes.num}>
                        {fmtPct(r.qualifiedWithin24hPct)}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <ButtonCell widget={r.widget} monoClass={classes.mono} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <LiveChip live={!!r.whatsappLive} />
                      </TableCell>
                      <TableCell className={classes.cell}>{fmtDate(r.lastBillingSyncAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}

export default GoogleAdsPilotConsolePage;
